import React from 'react';

type PrivacyBullet = {
  label?: string;
  text: string;
};

type PrivacySection = {
  title: string;
  intro?: string;
  paragraphs?: string[];
  bullets?: PrivacyBullet[];
  outro?: string;
};

const privacySections: PrivacySection[] = [
  {
    title: '1. Information We Collect',
    intro: 'We collect the following types of information:',
    bullets: [
      {
        label: 'Account and Contact Information',
        text: 'Name, email address, phone number (if provided), and login credentials when you create an account or subscribe.',
      },
      {
        label: 'Payment Information',
        text: 'Billing details processed by our third-party payment processors. We do not store full credit card numbers on our servers.',
      },
      {
        label: 'Usage and Device Data',
        text: 'IP address, browser type, device information, pages visited, time spent, and interactions with content, collected via cookies and similar technologies.',
      },
      {
        label: 'Community and User Content',
        text: 'Posts, comments, messages, and other content you share in our community, including on platforms such as Mighty Networks.',
      },
      {
        label: 'Communications',
        text: 'Information you provide when contacting us, responding to surveys, or participating in events.',
      },
      {
        label: 'Marketing Preferences',
        text: 'Your choices regarding newsletters and promotional communications.',
      },
    ],
  },
  {
    title: '2. How We Use Your Information',
    intro: 'We use your information to:',
    bullets: [
      { text: 'Provide, maintain, and improve the Service, including delivering research, stock insights, education, videos, and community access.' },
      { text: 'Process subscriptions, payments, and account management.' },
      { text: 'Communicate with you about your account, updates, and customer support.' },
      { text: 'Send marketing communications, with your consent where required.' },
      { text: 'Analyze usage to improve content and user experience.' },
      { text: 'Detect and prevent fraud, abuse, or security issues.' },
      { text: 'Comply with legal obligations.' },
    ],
  },
  {
    title: '3. Sharing of Information',
    intro: 'We do not sell your personal information. We may share it with:',
    bullets: [
      { text: 'Service providers, including payment processors, email delivery tools, community platforms, analytics providers, and hosting services, who process data on our behalf under confidentiality agreements.' },
      { text: 'Professional advisors or legal authorities when required by law or to protect our rights.' },
      { text: 'In connection with a business transfer, such as a merger or sale, with notice where required.' },
    ],
    outro: 'Community content you post may be visible to other members according to the platform’s settings.',
  },
  {
    title: '4. Cookies, Analytics, and Advertising',
    paragraphs: [
      'We use cookies and similar technologies for functionality, analytics, and advertising.',
    ],
    bullets: [
      { text: 'Essential cookies keep the site and account login working.' },
      { text: 'Analytics cookies help us understand how the site is used.' },
      { text: 'Advertising cookies and pixels, including Google Ads and the Meta Pixel if enabled, help us measure campaigns and show relevant ads. These partners may collect or receive information from the Service and use it under their own policies.' },
    ],
  },
  {
    title: '5. Data Retention and Security',
    paragraphs: [
      'We retain personal information only as long as necessary for the purposes described or as required by law. We implement reasonable technical and organizational measures to protect your data, but no method of transmission over the Internet is 100% secure.',
    ],
  },
  {
    title: '6. Your Rights',
    intro: 'Depending on where you live, you may have the right to:',
    bullets: [
      { text: 'Access the personal information we hold about you.' },
      { text: 'Request corrections to inaccurate information.' },
      { text: 'Withdraw consent, subject to legal or contractual restrictions.' },
      { text: 'Request deletion of your information in certain circumstances.' },
      { text: 'Lodge a complaint with a relevant privacy regulator, including, for Canadian residents, the Office of the Privacy Commissioner of Canada.' },
    ],
    outro: 'To exercise these rights, contact us at the details below. We will respond within a reasonable timeframe as required by applicable law.',
  },
  {
    title: '7. International Transfers',
    paragraphs: [
      'The Service is operated from the United States. Information may be processed in the United States and in other countries where our service providers are located. Those countries may have different data-protection rules than your own. By using the Service, you acknowledge that your information may be transferred and processed in those locations.',
    ],
  },
  {
    title: '8. Children’s Privacy',
    paragraphs: [
      'The Service is not directed to individuals under 18. We do not knowingly collect personal information from children.',
    ],
  },
  {
    title: '9. Changes to This Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. We will post the revised version on this page with an updated date. Continued use of the Service constitutes acceptance of the changes.',
    ],
  },
];

const advertisingOptOutLinks = [
  { label: 'Google Ads Settings', href: 'https://adssettings.google.com' },
  { label: 'Meta ad preferences', href: 'https://www.facebook.com/ads/preferences' },
  { label: 'Network Advertising Initiative', href: 'https://optout.networkadvertising.org' },
  { label: 'Digital Advertising Alliance', href: 'https://optout.aboutads.info' },
];

const PrivacyPolicy: React.FC = () => {
  return (
    <main className="min-h-screen bg-white text-[#17243a]">
      <section className="border-b border-gray-200 bg-[#f7f6f1]">
        <div className="mx-auto px-4 md:px-10 lg:px-32 w-full py-16 lg:py-24">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-[#0B6054]">Legal</p>
          <h1 className="mb-5 text-4xl font-bold tracking-tight text-[#1a2942] sm:text-5xl lg:text-6xl">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-700">Last Updated: October 9, 2026</p>
        </div>
      </section>

      <section className="mx-auto px-4 md:px-10 lg:px-32 w-full py-12 lg:py-16">
        <article className="prose prose-lg max-w-none prose-headings:text-[#1a2942] prose-p:text-gray-700 prose-li:text-gray-700">
          <p>
            The Wealthy Miner LLC (“we,” “us,” or “our”), a limited liability company incorporated in the State of Wyoming,
            United States, operates the website thewealthyminer.com and related subscription services, community platforms,
            and communications (collectively, the “Service”).
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose, and protect personal information. By using the Service,
            you consent to the practices described here. If you are located in Canada, certain rights under the Personal
            Information Protection and Electronic Documents Act (PIPEDA) may also apply. If you are located in another
            jurisdiction, local privacy laws may apply to the extent they cover our processing of your information.
          </p>

          {privacySections.map((section) => (
            <section key={section.title} className="mt-10">
              <h2 className="mb-4 text-2xl font-bold">{section.title}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.intro && <p>{section.intro}</p>}
              {section.bullets && (
                <ul className="list-disc space-y-2 pl-6">
                  {section.bullets.map((bullet) => (
                    <li key={`${section.title}-${bullet.text}`}>
                      {bullet.label && <strong>{bullet.label}: </strong>}
                      {bullet.text}
                    </li>
                  ))}
                </ul>
              )}
              {section.title === '4. Cookies, Analytics, and Advertising' && (
                <>
                  <p>You can control cookies through your browser settings. You can also opt out of interest-based advertising through:</p>
                  <ul className="list-disc space-y-2 pl-6">
                    {advertisingOptOutLinks.map((link) => (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-[#0B6054] underline underline-offset-4 hover:text-[#c9a227]"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p>Some features may not work if cookies are disabled.</p>
                </>
              )}
              {section.outro && <p>{section.outro}</p>}
            </section>
          ))}

          <section className="mt-10 rounded-2xl border border-gray-200 bg-[#f7f6f1] p-6 sm:p-8">
            <h2 className="mb-4 text-2xl font-bold">10. Contact Us</h2>
            <p>For privacy-related questions or requests:</p>
            <p className="mb-0">
              The Wealthy Miner LLC
              <br />
              A Wyoming limited liability company
              <br />
              Email:{' '}
              <a
                href="mailto:info@thewealthyminer.com"
                className="font-medium text-[#0B6054] underline underline-offset-4 hover:text-[#c9a227]"
              >
                info@thewealthyminer.com
              </a>
              <br />
              Website: thewealthyminer.com
            </p>
          </section>
        </article>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
